import { useCallback, useState, type ReactNode } from 'react';
import RejectEventModal from '../components/RejectEventModal';
import { api } from '../lib/api';
import { clearSwrCacheByPrefix } from '../lib/swrCache';
import type { ExtractedEvent } from '../types';
import { toast } from '../lib/dialogs';

interface UseEventRejectionOptions {
  /**
   * Called after the backend confirms the rejection. Use this to prune the
   * event from local state, close detail panels, etc. The hook already
   * clears the `events:` SWR cache for you.
   */
  onRejected?: (event: ExtractedEvent, flagForAiTraining: boolean) => void;
}

interface UseEventRejectionResult {
  /**
   * Open the in-app reject confirmation modal for the given event.
   * Caller is responsible for closing any other open modals first if needed.
   */
  promptReject: (event: ExtractedEvent) => void;
  /**
   * Render this somewhere in the page tree. The modal is hidden until
   * `promptReject` is called. Safe to render anywhere — it's portal-like
   * (fixed position) so order in the JSX tree doesn't matter.
   */
  modal: ReactNode;
}

/**
 * Shared admin "hide / reject" flow used by every list/card view of events
 * (home, map, venue detail, etc.). Encapsulates:
 *   1. The `rejectingEvent` state
 *   2. The `<RejectEventModal>` mount with its "Reject only" /
 *      "Reject & flag for AI training" choices
 *   3. The `POST /api/mgmt/events/:id/reject` API call (with the AI flag)
 *   4. Clearing the shared SWR `events:` cache so other windows refetch
 *
 * Pages provide an `onRejected` callback to do their own local cleanup
 * (e.g. filter the event out of an in-memory array, close a detail modal).
 *
 * Errors are surfaced via `window.alert` to match the previous behavior;
 * if we ever build a global toast system this is the single spot to swap.
 */
export function useEventRejection(opts: UseEventRejectionOptions = {}): UseEventRejectionResult {
  const { onRejected } = opts;
  const [rejectingEvent, setRejectingEvent] = useState<ExtractedEvent | null>(null);

  const promptReject = useCallback((event: ExtractedEvent) => {
    setRejectingEvent(event);
  }, []);

  const handleConfirm = useCallback(
    async (event: ExtractedEvent, flagForAiTraining: boolean) => {
      setRejectingEvent(null);
      try {
        // Use the dedicated reject endpoint so the backend records the
        // decision (and the AI-training flag) — `PATCH /events/{id}` with
        // `status: 'rejected'` would not pass the flag through.
        try {
          await api.post(`/api/mgmt/events/${encodeURIComponent(event.rowKey)}/reject`, {
            flaggedForAiTraining: flagForAiTraining,
          });
        } catch (err) {
          // Treat 404 as already-gone: the event was deleted/dedup'd by a
          // background job (cleanup-stale-recurring-instances, dedup scan,
          // hard delete) between when the SPA cached it and when the admin
          // clicked Hide. The user's intent — make it disappear — is already
          // satisfied; don't show a scary alert. Fall through to local
          // cleanup so the row vanishes from the UI.
          const status = (err as { status?: number } | null)?.status;
          if (status !== 404) throw err;
        }
        clearSwrCacheByPrefix('events:');
        onRejected?.(event, flagForAiTraining);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(`Could not hide event: ${msg}`);
      }
    },
    [onRejected],
  );

  const modal = (
    <RejectEventModal
      event={rejectingEvent}
      onClose={() => setRejectingEvent(null)}
      onConfirm={handleConfirm}
    />
  );

  return { promptReject, modal };
}
